import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface AnimatedCubeIconProps {
  size?: number;
  className?: string;
}

export function AnimatedCubeIcon({ size = 80, className = '' }: AnimatedCubeIconProps) {
  const [rotationY, setRotationY] = useState(-45);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotationY((prev) => prev + 1);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  const halfSize = size / 2;
  const containerSize = `${size}px`;
  const translateZ = `${halfSize}px`;
  const innerCubeSize = size * 0.95;
  const innerCubeSizeStr = `${innerCubeSize}px`;
  const innerCubeOffset = (size - innerCubeSize) / 2;

  return (
    <div className={cn('flex justify-center items-center py-4', className)}>
      <div
        className="relative select-none"
        style={{
          width: containerSize,
          height: containerSize,
          perspective: '1000px',
        }}
      >
        <div
          className="relative w-full h-full transition-transform duration-100"
          style={{
            transformStyle: 'preserve-3d',
            transform: `rotateX(-25deg) rotateY(${rotationY}deg)`,
          }}
        >
          {/* Black inner cube to fill gaps */}
          <div
            className="absolute bg-black"
            style={{
              width: innerCubeSizeStr,
              height: innerCubeSizeStr,
              left: `${innerCubeOffset}px`,
              top: `${innerCubeOffset}px`,
              transform: `translateZ(${halfSize * 0.95}px)`,
            }}
          />
          <div
            className="absolute bg-black"
            style={{
              width: innerCubeSizeStr,
              height: innerCubeSizeStr,
              left: `${innerCubeOffset}px`,
              top: `${innerCubeOffset}px`,
              transform: `rotateX(90deg) translateZ(${halfSize * 0.95}px)`,
            }}
          />
          <div
            className="absolute bg-black"
            style={{
              width: innerCubeSizeStr,
              height: innerCubeSizeStr,
              left: `${innerCubeOffset}px`,
              top: `${innerCubeOffset}px`,
              transform: `rotateX(-90deg) translateZ(${halfSize * 0.95}px)`,
            }}
          />
          <div
            className="absolute bg-black"
            style={{
              width: innerCubeSizeStr,
              height: innerCubeSizeStr,
              left: `${innerCubeOffset}px`,
              top: `${innerCubeOffset}px`,
              transform: `rotateY(90deg) translateZ(${halfSize * 0.95}px)`,
            }}
          />
          <div
            className="absolute bg-black"
            style={{
              width: innerCubeSizeStr,
              height: innerCubeSizeStr,
              left: `${innerCubeOffset}px`,
              top: `${innerCubeOffset}px`,
              transform: `rotateY(-90deg) translateZ(${halfSize * 0.95}px)`,
            }}
          />
          <div
            className="absolute bg-black"
            style={{
              width: innerCubeSizeStr,
              height: innerCubeSizeStr,
              left: `${innerCubeOffset}px`,
              top: `${innerCubeOffset}px`,
              transform: `rotateY(180deg) translateZ(${halfSize * 0.95}px)`,
            }}
          />

          {/* Top face (pink) */}
          <div
            className="absolute flex items-center justify-center rounded-lg border-2 border-black"
            style={{
              width: containerSize,
              height: containerSize,
              transform: `rotateX(90deg) translateZ(${translateZ})`,
              backgroundColor: '#FF11CB',
            }}
          />

          {/* Bottom face (pink) */}
          <div
            className="absolute flex items-center justify-center rounded-lg border-2 border-black"
            style={{
              width: containerSize,
              height: containerSize,
              transform: `rotateX(-90deg) translateZ(${translateZ})`,
              backgroundColor: '#FF11CB',
            }}
          />

          {/* Left face (blue) */}
          <div
            className="absolute flex items-center justify-center rounded-lg border-2 border-black"
            style={{
              width: containerSize,
              height: containerSize,
              transform: `rotateY(-90deg) translateZ(${translateZ})`,
              backgroundColor: '#0AA5EA',
            }}
          />

          {/* Right face (blue) */}
          <div
            className="absolute flex items-center justify-center rounded-lg border-2 border-black"
            style={{
              width: containerSize,
              height: containerSize,
              transform: `rotateY(90deg) translateZ(${translateZ})`,
              backgroundColor: '#0AA5EA',
            }}
          />

          {/* Front face (yellow) */}
          <div
            className="absolute flex items-center justify-center rounded-lg border-2 border-black"
            style={{
              width: containerSize,
              height: containerSize,
              transform: `translateZ(${translateZ})`,
              backgroundColor: '#FFB50D',
            }}
          />

          {/* Back face (yellow) */}
          <div
            className="absolute flex items-center justify-center rounded-lg border-2 border-black"
            style={{
              width: containerSize,
              height: containerSize,
              transform: `rotateY(180deg) translateZ(${translateZ})`,
              backgroundColor: '#FFB50D',
            }}
          />
        </div>
      </div>
    </div>
  );
}
